import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { BookOpen, Upload, Send, Check, X, FileText, Search, Filter, Download, Edit, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getBookCover } from '@/lib/assets';

const curatedTitles = [
  { id: 1, title: 'The Wonders of Coding', author: 'Sarah Chen', publisher: 'MC Edu', year: 2024, level: 'Sec', category: 'NF EL', price: 18.50, status: 'approved', isbn: '978-981-12345-16' },
  { id: 2, title: 'Climate Change & You', author: 'David Atkins', publisher: 'Nat. Geo', year: 2025, level: 'Sec', category: 'NF EL', price: 14.20, status: 'approved', isbn: '978-1-4262-0123' },
  { id: 3, title: 'Singapore Stories Vol 3', author: 'Various', publisher: 'NLB', year: 2025, level: 'All', category: 'NF EL', price: 22.50, status: 'approved', isbn: '978-981-12345-2' },
  { id: 4, title: 'Between Two Worlds', author: 'Amelia Tan', publisher: 'Epigram', year: 2025, level: 'Sec', category: 'Fiction EL', price: 16.80, status: 'approved', isbn: '978-981-12345-9' },
  { id: 5, title: 'The Last Library', author: 'Priya Nair', publisher: 'Penguin', year: 2024, level: 'Sec', category: 'Fiction EL', price: 15.90, status: 'pending', isbn: '978-981-12345-10' },
  { id: 6, title: 'Semangat!', author: 'Hanna Abdullah', publisher: 'DBP', year: 2024, level: 'Sec', category: 'MTL-ML', price: 16.80, status: 'approved', isbn: '978-983-12345-3' },
  { id: 7, title: 'Nam Kathaigal', author: 'Priya Devi', publisher: 'Grassroots', year: 2024, level: 'Pri', category: 'MTL-TL', price: 15.40, status: 'pending', isbn: '978-981-12345-4' },
  { id: 8, title: 'Roots & Routes', author: 'David Tan', publisher: 'NUS Press', year: 2025, level: 'Sec', category: 'NF EL', price: 24.00, status: 'approved', isbn: '978-981-12345-7' },
];

const rejectedTitles = [
  { 
    id: 10, 
    title: 'Psychology of Success', 
    author: 'James Lim',
    publisher: 'Pearson',
    year: 2021,
    reason: 'Not age-appropriate (adult tone)', 
    dateRejected: '25 Apr 2026', 
    deadline: '30 Apr 2026', 
    deadlineUrgent: true,
    replacementTitle: "Mindset Matters: A Teen's Guide",
    replacementAuthor: 'Dr Serene Tan',
    replacementPublisher: 'Times',
    replacementYear: 2024,
    replacementStatus: 'ready'
  },
  { 
    id: 11, 
    title: 'Living with Purpose', 
    author: 'Pastor John',
    publisher: 'Faith Press',
    year: 2023,
    reason: 'Fails MOE Guidelines (religious content)', 
    dateRejected: '24 Apr 2026', 
    deadline: '29 Apr 2026', 
    deadlineUrgent: false,
    replacementTitle: 'The Courage Code',
    replacementAuthor: 'Maya Singh',
    replacementPublisher: 'Penguin',
    replacementYear: 2024,
    replacementStatus: 'submitted'
  },
  { 
    id: 12, 
    title: 'Advanced Chemistry Sec', 
    author: 'Prof Tan',
    publisher: 'Cambridge',
    year: 2024,
    reason: 'Not right reading level (Pre-U content)', 
    dateRejected: '23 Apr 2026', 
    deadline: '28 Apr 2026', 
    deadlineUrgent: false,
    replacementTitle: 'Chemistry Simplified',
    replacementAuthor: 'Prof Wong',
    replacementPublisher: 'MC Edu',
    replacementYear: 2025,
    replacementStatus: 'submitted'
  },
];

const moeSuggestions = [
  { id: 20, title: 'Girls Who Code', author: 'Reshma Saujani', publisher: 'Penguin', suggestedBy: 'RGS Library', level: 'Sec', status: 'suitable', notes: 'Matches STEM focus' },
  { id: 21, title: 'Malay Proverbs for Kids', author: 'Ahmad Yusof', publisher: 'DBP', suggestedBy: 'Queenstown Pri', level: 'Pri', status: 'under_review', notes: 'Checking age appropriateness' },
  { id: 22, title: 'Digital Citizenship 101', author: 'Sarah Lim', publisher: 'Times', suggestedBy: 'MOE Curriculum', level: 'Sec', status: 'suitable', notes: 'Recommended for Cycle 2' },
  { id: 23, title: 'Young Entrepreneurs SG', author: 'Various', publisher: 'NUS Press', suggestedBy: 'ACS(I)', level: 'Sec', status: 'rejected', notes: 'Too narrow focus' },
];

export default function CivicaCurationManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const handleSubmitReplacement = (title) => {
    toast.success(`Replacement "${title.replacementTitle}" submitted to MOE HQ`);
  };

  const filteredTitles = curatedTitles.filter(title => {
    const matchesSearch = title.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         title.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = levelFilter === 'all' || title.level === levelFilter;
    const matchesCategory = categoryFilter === 'all' || title.category === categoryFilter;
    return matchesSearch && matchesLevel && matchesCategory;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-[#2E5F8A]" />
          Curation Management — CDS Cycle 1 2028
        </h1>
        <p className="text-slate-500 mt-1">Lead Curator: Ms Priya Devi | Deadline: 30 Apr 2026</p>
      </div>

      {/* Alert Banner */}
      <Card className="bg-red-50 border-red-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800 font-medium">
              1 replacement title due TODAY. Submit immediately to avoid penalty.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2 flex-wrap">
        <Badge className="bg-slate-100 text-slate-700">Target: 228</Badge>
        <Badge className="bg-emerald-100 text-emerald-700">Submitted: 228/228 ✓</Badge>
        <Badge className="bg-emerald-100 text-emerald-700">Approved: 194</Badge>
        <Badge className="bg-amber-100 text-amber-700">Pending MOE: 34</Badge>
        <Badge className="bg-red-100 text-red-700">Rejected: 3</Badge>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="list" data-testid="tab-title-list">Title List (228)</TabsTrigger>
          <TabsTrigger value="add" data-testid="tab-add-title">Add/Edit Title</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected & Replacements (3)</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">MOE Suggestions (4)</TabsTrigger>
          <TabsTrigger value="submit" data-testid="tab-submit">Submit to MOE HQ</TabsTrigger>
        </TabsList>

        {/* Title List Tab */}
        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Curated Titles</CardTitle>
                <CardDescription>All titles for CDS Cycle 1 2028</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export List
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-6 items-center">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search title or author..." 
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="Pri">Primary</SelectItem>
                    <SelectItem value="Sec">Secondary</SelectItem>
                    <SelectItem value="All">All</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="NF EL">NF EL</SelectItem>
                    <SelectItem value="Fiction EL">Fiction EL</SelectItem>
                    <SelectItem value="MTL-CL">MTL-CL</SelectItem>
                    <SelectItem value="MTL-ML">MTL-ML</SelectItem>
                    <SelectItem value="MTL-TL">MTL-TL</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead className="w-16">Cover</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Publisher</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTitles.map((title, idx) => (
                    <TableRow key={title.id} className="table-row-striped">
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <img 
                          src={getBookCover(title.title)} 
                          alt={title.title}
                          className="w-10 h-14 object-cover rounded"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{title.title}</TableCell>
                      <TableCell>{title.author}</TableCell>
                      <TableCell>{title.publisher}</TableCell>
                      <TableCell>{title.level}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{title.category}</Badge>
                      </TableCell>
                      <TableCell>${title.price.toFixed(2)}</TableCell>
                      <TableCell>
                        {title.status === 'approved' && (
                          <Badge className="bg-emerald-50 text-emerald-700">Approved</Badge>
                        )}
                        {title.status === 'pending' && (
                          <Badge className="bg-amber-50 text-amber-700">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing {filteredTitles.length} of 228 titles</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Add/Edit Title Tab */}
        <TabsContent value="add" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Add New Title</CardTitle>
              <CardDescription>Add a new book to the curated list for MOE approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Input placeholder="Title *" />
                <Input placeholder="Author *" />
                <Input placeholder="Publisher *" />
                <Input placeholder="Year *" type="number" />
                <Input placeholder="ISBN *" />
                <Input placeholder="Price *" type="number" step="0.01" />
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Level *" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Primary">Primary</SelectItem>
                    <SelectItem value="Secondary">Secondary</SelectItem>
                    <SelectItem value="Pre-U">Pre-U</SelectItem>
                    <SelectItem value="All">All Levels</SelectItem>
                  </SelectContent>
                </Select>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Category *" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NF EL">Non-Fiction EL</SelectItem>
                    <SelectItem value="Fiction EL">Fiction EL</SelectItem>
                    <SelectItem value="MTL-CL">MTL Chinese</SelectItem>
                    <SelectItem value="MTL-ML">MTL Malay</SelectItem>
                    <SelectItem value="MTL-TL">MTL Tamil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button className="bg-[#2E5F8A] hover:bg-[#254B73]">
                  <Check className="w-4 h-4 mr-2" />
                  Save Title
                </Button>
                <Button variant="outline">
                  Save & Add Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected & Replacements Tab */}
        <TabsContent value="rejected" className="mt-6">
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader>
              <CardTitle className="text-red-800 font-heading">Replacement Required — Action Needed</CardTitle>
              <CardDescription>3 titles rejected by MOE HQ. Submit replacements before deadline.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rejectedTitles.map((title) => (
                  <div key={title.id} className={`p-4 bg-white rounded-lg border ${title.deadlineUrgent ? 'border-red-300' : 'border-slate-200'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900">"{title.title}"</h4>
                          {title.deadlineUrgent && (
                            <Badge variant="destructive">DUE TODAY</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{title.author} | {title.publisher} | {title.year}</p>
                        <p className="text-sm text-red-600 mt-1">Rejected: {title.reason}</p>
                        <p className="text-xs text-slate-500">Deadline: {title.deadline}</p>
                      </div>
                      {title.replacementStatus === 'submitted' ? (
                        <Badge className="bg-emerald-50 text-emerald-700">Submitted ✓</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700">Ready to Submit</Badge>
                      )}
                    </div>
                    
                    {/* Replacement Info */}
                    <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800 mb-1">Proposed Replacement:</p>
                      <p className="font-semibold text-emerald-900">"{title.replacementTitle}"</p>
                      <p className="text-sm text-emerald-700">{title.replacementAuthor} | {title.replacementPublisher} | {title.replacementYear}</p>
                    </div>

                    {title.replacementStatus === 'ready' && (
                      <Button 
                        className="mt-3 bg-[#2E5F8A] hover:bg-[#254B73]" 
                        size="sm"
                        onClick={() => handleSubmitReplacement(title)}
                        data-testid={`submit-replacement-${title.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit Replacement to MOE HQ
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex gap-2">
                <Button 
                  className="bg-[#2E5F8A] hover:bg-[#254B73]"
                  onClick={() => toast.success('All pending replacements submitted')}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit All Pending Replacements
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MOE Suggestions Tab */}
        <TabsContent value="suggestions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">MOE & School Suggestions</CardTitle>
              <CardDescription>Review title suggestions from MOE HQ and participating schools</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Publisher</TableHead>
                    <TableHead>Suggested By</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {moeSuggestions.map((suggestion) => (
                    <TableRow key={suggestion.id} className="table-row-striped">
                      <TableCell className="font-medium">{suggestion.title}</TableCell>
                      <TableCell>{suggestion.author}</TableCell>
                      <TableCell>{suggestion.publisher}</TableCell>
                      <TableCell>{suggestion.suggestedBy}</TableCell>
                      <TableCell>{suggestion.level}</TableCell>
                      <TableCell>
                        {suggestion.status === 'suitable' && (
                          <Badge className="bg-emerald-50 text-emerald-700">Suitable</Badge>
                        )}
                        {suggestion.status === 'under_review' && (
                          <Badge className="bg-amber-50 text-amber-700">Under Review</Badge>
                        )}
                        {suggestion.status === 'rejected' && (
                          <Badge className="bg-red-50 text-red-700">Not Suitable</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[150px]">{suggestion.notes}</TableCell>
                      <TableCell>
                        {suggestion.status === 'suitable' && (
                          <Button size="sm" variant="outline">
                            Add to Cycle 2
                          </Button>
                        )}
                        {suggestion.status === 'under_review' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="text-emerald-600">
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submit to MOE HQ Tab */}
        <TabsContent value="submit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Submit to MOE HQ</CardTitle>
              <CardDescription>Submit curated titles for MOE approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-700">225</div>
                    <div className="text-sm text-emerald-600">Submitted & Approved</div>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700">3</div>
                    <div className="text-sm text-amber-600">Replacements Pending</div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Submission Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Non-Fiction EL</span>
                      <span>68 titles ✓</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Fiction EL</span>
                      <span>62 titles ✓</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Chinese</span>
                      <span>48 titles ✓</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Malay</span>
                      <span>32 titles ✓</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Tamil</span>
                      <span>18 titles ✓</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button className="bg-[#2E5F8A] hover:bg-[#254B73]">
                    <Upload className="w-4 h-4 mr-2" />
                    Submit All Pending Titles to MOE HQ
                  </Button>
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download Submission Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
